/* eslint-disable max-lines-per-function */

import { Fragment, useEffect, useRef, useState } from 'react';
import { PreviewChapterLayout } from './PreviewChapterLayout';
import { PreviewIntroLayout } from '../../components/PreviewIntroLayout';
import { PreviewQuestion } from '../../components/PreviewQuestion';
import { PreviewResultLayout } from './PreviewResultLayout';
import { PreviewProduct } from '../../components/PreviewProduct';
import { PreviewContentLayout } from '../../components/PreviewContentLayout';
import { PreviewDynamicContactForm } from '../../components/PreviewDynamicContactForm';
import { PreviewResultDepotLayout } from '../../components/PreviewResultDepotLayout';
import { ComparisonTablePreview } from '../../../../modules/ComparisonTableLayout';
import { ChapterState, TemplateTypes } from '../../../../../../../../lib/models/TemplateTypes';
import { isLength, safeJsonParse } from '../../../../../../../../lib/util/helpers';
import { PreviewForm } from '../../components/PreviewForm';
import { PreviewContextProvider } from '../../PreviewContext';
import { Form } from 'antd';
import { NextPrevButtons } from '../components/Buttons/NextPrevButtons';
import { elementRulesEngine } from '../../../../utils/rules/element_rules_engine_helper';
import { chaptersRulesEngine } from '../../../../utils/rules/chapters_rules_engine_helper';
import { productSelectionRulesEngine } from '../../../../utils/rules/products_selection_rules_engine_helper';
import { rulesEngine } from 'src/processes/CMS/processes/management/utils/rules/rules_helper';
import { useVariables } from 'src/processes/CMS/processes/management/pages/PreviewPage/hooks/useVariables';
import { answerSetRulesEngine } from '../../../../utils/rules/answer_set_rules_engine_helper';
import { answerSelectionRulesEngine } from '../../../../utils/rules/answer_selection_rules_engine_helper';
import styles from './styles.module.scss';
import { ResultButton } from '../components/Buttons/ResultButton';

const DELAY = 100;

export function ProductBasedPreviewLayoutTemplates({
  media,
  chapterIconMedia,
  pagesFlow,
  setPagesFlow,
  fonts,
  colors,
  bulletIcons,
  fontsForLoading,
  showChapterIds,
  setShowChapterIds,
  isTesting,
  setSidebarVisible,
  pageHistory,
  setPageHistory,
  currentElements,
  audioGuideModal,
  isAutoGuide,
  footer,
  imageSelectionFlow,
  setImageSelectionFlow,
}) {
  const { processVariables } = useVariables(pagesFlow, setPagesFlow);
  const [form] = Form.useForm();
  const finderUrl = pagesFlow?.finder?.url;
  const finderType = pagesFlow?.finder?.finderType;

  const firstFocusableRef = useRef(null);

  const [previewState, setPreviewState] = useState({
    form,
    finderUrl,
    isFormSubmitting: false,
    finderType,
  });

  const [allowRulesProductSelection, setAllowRulesProductSelection] = useState([]);
  const [allowRulesAnswerSelection, setAllowRulesAnswerSelection] = useState([]);
  const [rulesSetAnswerValues, setRulesSetAnswerValues] = useState([]);
  const [showCompletedChapters, setShowCompletedChapters] = useState(false);

  const {
    allPages,
    currentPageId,
    // currentElementsIds,
    showContactForm,
    allChapters,
    resultTableContent,
    clickedButtonsIds,
  } = pagesFlow;

  const currentPage = allPages.find(page => page.id === currentPageId);
  const isDisabled = currentElements.some(el => el.content.mandatory && !isLength(el.selectedAnswers));
  const genericProps = {
    colors,
    fonts,
    pagesFlow,
    setPagesFlow,
    allPages,
    setPreviewState,
    fontsForLoading,
    setPageHistory,
    pageHistory,
    currentElements,
    showChapterIds,
    chapterIconMedia,
    imageSelectionFlow,
    footer,
  };

  const allCurrentElementsRules = currentElements.filter(el => el.templateType !== TemplateTypes.CHAPTER).flatMap(el => el.rules);
  const allChaptersRules = allChapters.flatMap(el => el.rules);

  useEffect(() => {
    if (finderUrl || finderType) {
      setPreviewState(prev => ({ ...prev, finderUrl, finderType }));
    }
  }, [finderUrl, finderType]);

  useEffect(() => {
    if (isLength(allCurrentElementsRules)) {
      elementRulesEngine(allCurrentElementsRules, pagesFlow, setPagesFlow, currentElements);
      const allRules = allPages.flatMap(page => page.elements.filter(el => el.rules.length)).flatMap(singlePage => singlePage.rules);
      const { resultVariables } = rulesEngine({
        rules: allRules,
        pagesFlow,
        currentElements,
        rulesFilter: ['resultVariables'],
      });
      processVariables(resultVariables);
    }
    if (isLength(allChaptersRules) && isLength(showChapterIds)) {
      chaptersRulesEngine(allChaptersRules, pagesFlow, showChapterIds, setShowChapterIds, currentElements, setImageSelectionFlow, setPagesFlow);
    } else {
      // all chapters hidden: drop pictures of the last engine run, keep the same array when it is already empty to skip a rerender
      setImageSelectionFlow(prev => (isLength(prev) ? [] : prev));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPages, currentPageId, isLength(allCurrentElementsRules), clickedButtonsIds]);

  useEffect(() => {
    if (isLength(allCurrentElementsRules)) {
      productSelectionRulesEngine(
        allCurrentElementsRules,
        pagesFlow,
        setPagesFlow,
        currentElements,
        allowRulesProductSelection,
        setAllowRulesProductSelection
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPages, allCurrentElementsRules, currentPageId, clickedButtonsIds]);

  useEffect(() => {
    if (isLength(allCurrentElementsRules)) {
      answerSetRulesEngine(allCurrentElementsRules, pagesFlow, setPagesFlow, currentElements, rulesSetAnswerValues, setRulesSetAnswerValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPages, currentPageId, clickedButtonsIds]);

  useEffect(() => {
    if (isLength(allCurrentElementsRules)) {
      answerSelectionRulesEngine(
        allCurrentElementsRules,
        pagesFlow,
        setPagesFlow,
        currentElements,
        allowRulesAnswerSelection,
        setAllowRulesAnswerSelection
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPages, allCurrentElementsRules, currentPageId, clickedButtonsIds]);

  useEffect(() => {
    document.addEventListener(
      'mousedown',
      event => {
        if (event.detail > 1) {
          event.preventDefault();
        }
      },
      false
    );
  }, []);

  const getResultLayout = el => {
    if (el.content.products) {
      return <PreviewResultDepotLayout {...genericProps} resultObject={el} style={JSON.parse(el.layoutStyle)} />;
    }
    return <PreviewResultLayout {...genericProps} resultObject={el} style={JSON.parse(el.layoutStyle)} />;
  };

  const noPaddingTypes = new Set(['INTRO', 'CHAPTER']);
  const hasPadding = currentElements?.some(el => !noPaddingTypes.has(el.templateType));

  useEffect(() => {
    setTimeout(() => {
      firstFocusableRef.current?.focus();
    }, DELAY);
  }, [currentPage?.id, audioGuideModal]);

  const getLayoutStyleForChapter = () => {
    const completedChapter = currentPage?.elements.find(el => el?.content?.completedChaptersButtons);
    if (completedChapter && showCompletedChapters) {
      return safeJsonParse(completedChapter.layoutStyle);
    }
    return safeJsonParse(currentPage?.elements?.[0]?.layoutStyle);
  };

  const layoutStyleForChapter = getLayoutStyleForChapter();

  useEffect(() => {
    const allowed = new Set([TemplateTypes.QUESTION, TemplateTypes.PRODUCT, TemplateTypes.CONTENT]);

    const shouldShow = Array.isArray(currentElements) && currentElements.some(el => allowed.has(el.templateType));

    setSidebarVisible?.(Boolean(shouldShow));

    return () => {
      setSidebarVisible?.(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageId, currentElements?.length, clickedButtonsIds]);

  const isChapter = currentElements.some(el => el?.templateType === TemplateTypes.CHAPTER);

  return (
    <PreviewContextProvider value={{ previewState, setPreviewState, pageHistory }}>
      <div
        className={styles.container}
        style={{
          paddingBottom: hasPadding ? '88px' : 0,
          // backgroundColor: colors.backgroundColorSecond,
          backgroundColor: isChapter ? colors.backgroundColorSecond : colors.backgroundColor,
        }}
        ref={firstFocusableRef}
        tabIndex={0}
        role='button'
      >
        {currentPage?.templateType === TemplateTypes.CHAPTER ? (
          <PreviewChapterLayout
            media={media}
            chapterIconMedia={chapterIconMedia}
            bulletIcons={bulletIcons}
            pagesFlow={pagesFlow}
            setPagesFlow={setPagesFlow}
            colors={colors}
            fonts={fonts}
            style={layoutStyleForChapter}
            setPageHistory={setPageHistory}
            currentElements={currentElements}
            imageSelectionFlow={imageSelectionFlow}
            showChapterIds={showChapterIds}
            showCompletedChapters={showCompletedChapters}
            setShowCompletedChapters={setShowCompletedChapters}
            audioGuideModal={audioGuideModal}
            isAutoGuide={isAutoGuide}
          />
        ) : (
          currentElements?.map(el => {
            return (
              <Fragment key={el.id}>
                {el.templateType === TemplateTypes.INTRO && (
                  <PreviewIntroLayout
                    media={media}
                    introObject={el}
                    colors={colors}
                    fonts={fonts}
                    style={JSON.parse(el.layoutStyle)}
                    pagesFlow={pagesFlow}
                    setPagesFlow={setPagesFlow}
                    setPageHistory={setPageHistory}
                    setShowChapterIds={setShowChapterIds}
                    currentElements={currentElements}
                  />
                )}
                {el.templateType === TemplateTypes.QUESTION && (
                  <PreviewQuestion
                    key={el.id}
                    questionObject={el}
                    colors={colors}
                    fonts={fonts}
                    style={JSON.parse(el.layoutStyle)}
                    pagesFlow={pagesFlow}
                    setPagesFlow={setPagesFlow}
                    currentPageElements={currentPage?.elements}
                    setPageHistory={setPageHistory}
                    currentElements={currentElements}
                    isProductBased
                  />
                )}
                {el.templateType === TemplateTypes.PRODUCT && (
                  <PreviewProduct
                    productObject={el}
                    colors={colors}
                    fonts={fonts}
                    bulletIcons={bulletIcons}
                    style={JSON.parse(el.layoutStyle)}
                    pagesFlow={pagesFlow}
                    setPagesFlow={setPagesFlow}
                    currentElements={currentElements}
                    currentPageElements={currentPage?.elements}
                    setPageHistory={setPageHistory}
                    isTesting={isTesting}
                    isProductBased
                  />
                )}
                {el.templateType === TemplateTypes.RESULT &&
                  (showContactForm ? (
                    <PreviewForm {...genericProps} resultObject={el} style={JSON.parse(el.layoutStyle)} fonts={fonts} />
                  ) : (
                    getResultLayout(el)
                  ))}
                {el.templateType === TemplateTypes.CONTENT &&
                  (isLength(el?.content?.tables) ? (
                    <ComparisonTablePreview pagesFlow={pagesFlow} dataObj={el} colors={colors} fonts={fonts} />
                  ) : (
                    <PreviewContentLayout
                      contentObject={el}
                      colors={colors}
                      fonts={fonts}
                      media={media}
                      bulletIcons={bulletIcons}
                      style={JSON.parse(el.layoutStyle)}
                      pagesFlow={pagesFlow}
                      setPagesFlow={setPagesFlow}
                      setPageHistory={setPageHistory}
                      currentElements={currentElements}
                    />
                  ))}
                {el.templateType === TemplateTypes.CONTACT_FORM && (
                  <PreviewDynamicContactForm
                    {...genericProps}
                    colors={colors}
                    contactFormObject={el}
                    resultTableContent={resultTableContent}
                    style={JSON.parse(el.layoutStyle)}
                    form={form}
                    finderUrl={previewState.finderUrl}
                    currentElements={currentElements}
                  />
                )}
              </Fragment>
            );
          })
        )}
      </div>
      {currentPage?.templateType !== TemplateTypes.CHAPTER && currentElements[0]?.templateType !== TemplateTypes.INTRO && (
        <NextPrevButtons
          setPagesFlow={setPagesFlow}
          pagesFlow={pagesFlow}
          colors={colors}
          disabled={isDisabled}
          fonts={fonts}
          setPageHistory={setPageHistory}
          pageHistory={pageHistory}
          currentElements={currentElements}
        />
      )}
      {currentPage?.templateType === TemplateTypes.CHAPTER && allChapters.every(chapter => chapter?.state === ChapterState.COMPLETED) && (
        <ResultButton setPagesFlow={setPagesFlow} pagesFlow={pagesFlow} colors={colors} fonts={fonts} />
      )}
    </PreviewContextProvider>
  );
}
